/*
===============================================================================
VLETTE HOME HERO JS
Reusable controller for V·lette homepage hero video loading, autoplay retries,
poster reveal, responsive desktop/mobile media switching and Shopify editor reloads.
===============================================================================
*/

(function() {
  'use strict';

  if (window.VletteHomeHero && typeof window.VletteHomeHero.initAll === 'function') {
    window.VletteHomeHero.initAll(document);
    return;
  }

  var HERO_SELECTOR = '[data-section-type="vlette-home-hero"], [data-vlette-home-hero]';
  var instances = new WeakMap();

  function createHero(section) {
    var mobileQuery = window.matchMedia('(max-width: 768px)');
    var resizeTimer = null;
    var retryTimer = null;
    var interactionEventsBound = false;
    var cleanups = [];

    function addListener(target, eventName, handler, options) {
      if (!target || !target.addEventListener) {
        return;
      }

      target.addEventListener(eventName, handler, options);
      cleanups.push(function() {
        target.removeEventListener(eventName, handler, options);
      });
    }

    function getActiveItem() {
      return section.querySelector(
        mobileQuery.matches
          ? '.vlette-home-hero__media-item--mobile'
          : '.vlette-home-hero__media-item--desktop'
      );
    }

    function getFrameFromElement(element) {
      return element ? element.closest('[data-hero-media-frame]') : null;
    }

    function revealFrame(frame) {
      if (!frame) {
        return;
      }

      frame.classList.add('is-media-ready');
      section.classList.add('is-hero-ready');
    }

    function revealVideo(video) {
      revealFrame(getFrameFromElement(video));
    }

    function isActiveVideo(video) {
      var activeItem = getActiveItem();
      return Boolean(activeItem && video && activeItem.contains(video));
    }

    function prepareVideoForInlineAutoplay(video) {
      if (!video) {
        return;
      }

      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;

      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('data-type', 'mp4');

      video.removeAttribute('controls');
    }

    function getVideoSource(video) {
      if (!video) {
        return '';
      }

      if (video.currentSrc) {
        return video.currentSrc;
      }

      if (video.getAttribute('src')) {
        return video.getAttribute('src');
      }

      var source = video.querySelector('source[src]');
      return source ? source.getAttribute('src') : '';
    }

    function scheduleVideoRetry(video, attemptsRemaining, delay) {
      if (!video || attemptsRemaining <= 0) {
        return;
      }

      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(function() {
        playHeroVideo(video, attemptsRemaining - 1);
      }, delay || 500);
    }

    function playHeroVideo(video, attemptsRemaining) {
      var playPromise = null;

      if (!video || !isActiveVideo(video)) {
        return;
      }

      if (typeof attemptsRemaining !== 'number') {
        attemptsRemaining = 5;
      }

      prepareVideoForInlineAutoplay(video);

      if (!getVideoSource(video)) {
        revealVideo(video);
        return;
      }

      if (video.readyState >= 2) {
        revealVideo(video);
      }

      try {
        playPromise = video.play();
      } catch (error) {
        if (attemptsRemaining > 0) {
          scheduleVideoRetry(video, attemptsRemaining, 520);
        } else if (video.readyState >= 2) {
          revealVideo(video);
        }
        return;
      }

      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(function() {
            revealVideo(video);
          })
          .catch(function() {
            if (video.readyState >= 2) {
              revealVideo(video);
            }

            if (attemptsRemaining > 0 && !document.hidden) {
              scheduleVideoRetry(video, attemptsRemaining, attemptsRemaining > 2 ? 420 : 850);
            }
          });
      } else {
        revealVideo(video);
      }
    }

    function bindVideoEvents(video) {
      if (!video || video.getAttribute('data-hero-video-bound') === 'true') {
        return;
      }

      video.setAttribute('data-hero-video-bound', 'true');

      ['loadeddata', 'canplay', 'canplaythrough', 'playing', 'timeupdate'].forEach(function(eventName) {
        addListener(video, eventName, function() {
          revealVideo(video);

          if (isActiveVideo(video) && video.paused && !document.hidden) {
            playHeroVideo(video, 3);
          }
        });
      });

      addListener(video, 'pause', function() {
        if (isActiveVideo(video) && !document.hidden && !video.ended) {
          scheduleVideoRetry(video, 3, 500);
        }
      });

      addListener(video, 'stalled', function() {
        if (video.readyState >= 2) {
          revealVideo(video);
        }

        if (isActiveVideo(video) && !document.hidden) {
          scheduleVideoRetry(video, 3, 700);
        }
      });

      addListener(video, 'error', function() {
        section.classList.add('is-hero-video-error');
      });
    }

    function loadVideoItem(item, video) {
      if (!item || !video) {
        return;
      }

      item.setAttribute('data-hero-loaded', 'true');
      prepareVideoForInlineAutoplay(video);
      bindVideoEvents(video);

      if (video.readyState >= 2) {
        revealVideo(video);
      }

      playHeroVideo(video, 6);

      window.setTimeout(function() {
        playHeroVideo(video, 5);
      }, 250);

      window.setTimeout(function() {
        playHeroVideo(video, 4);
      }, 850);

      window.setTimeout(function() {
        playHeroVideo(video, 3);
      }, 1700);

      window.setTimeout(function() {
        if (video.readyState >= 2 || !video.paused) {
          revealVideo(video);
        }
      }, 2200);
    }

    function loadMediaItem(item) {
      var video;
      var frame;
      var iframe;
      var fallbackImage;
      var fallbackRevealTimer;

      if (!item) {
        return;
      }

      video = item.querySelector('video[data-hero-mp4-video], video');

      if (video) {
        loadVideoItem(item, video);
        return;
      }

      if (item.getAttribute('data-hero-loaded') === 'true') {
        return;
      }

      item.setAttribute('data-hero-loaded', 'true');

      frame = item.querySelector('[data-hero-media-frame]');
      iframe = item.querySelector('iframe[data-src]');
      fallbackImage = item.querySelector('[data-hero-fallback-image]');
      fallbackRevealTimer = window.setTimeout(function() {
        revealFrame(frame);
      }, 1800);

      function markReady() {
        window.clearTimeout(fallbackRevealTimer);
        revealFrame(frame);
      }

      if (iframe) {
        var iframeSource = iframe.getAttribute('data-src');

        if (iframeSource) {
          addListener(iframe, 'load', markReady, { once: true });
          iframe.setAttribute('src', iframeSource);
          iframe.removeAttribute('data-src');
        } else {
          markReady();
        }

        return;
      }

      if (fallbackImage) {
        if (fallbackImage.complete) {
          markReady();
        } else {
          addListener(fallbackImage, 'load', markReady, { once: true });
          addListener(fallbackImage, 'error', markReady, { once: true });
        }

        return;
      }

      markReady();
    }

    function pauseInactiveVideos() {
      var items = section.querySelectorAll('[data-hero-media-item]');
      var activeItem = getActiveItem();

      items.forEach(function(item) {
        if (item === activeItem) {
          return;
        }

        var video = item.querySelector('video');

        if (video && !video.paused) {
          video.pause();
        }
      });
    }

    function retryActiveVideoPlayback() {
      var activeItem = getActiveItem();
      var activeVideo = activeItem ? activeItem.querySelector('video') : null;

      if (activeVideo) {
        playHeroVideo(activeVideo, 5);
      }
    }

    function bindInteractionRetryEvents() {
      if (interactionEventsBound) {
        return;
      }

      interactionEventsBound = true;

      ['touchstart', 'pointerdown', 'click', 'scroll'].forEach(function(eventName) {
        addListener(document, eventName, retryActiveVideoPlayback, {
          passive: true,
          once: true
        });
      });
    }

    function initialiseHero() {
      var activeItem = getActiveItem();

      loadMediaItem(activeItem);
      pauseInactiveVideos();
      retryActiveVideoPlayback();
      bindInteractionRetryEvents();
    }

    function onResize() {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(initialiseHero, 180);
    }

    function onPageshow() {
      initialiseHero();
    }

    function onFocus() {
      retryActiveVideoPlayback();
    }

    function onVisibilityChange() {
      if (!document.hidden) {
        initialiseHero();
      }
    }

    function onMediaQueryChange() {
      initialiseHero();
    }

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', onMediaQueryChange);
      cleanups.push(function() {
        mobileQuery.removeEventListener('change', onMediaQueryChange);
      });
    } else if (mobileQuery.addListener) {
      mobileQuery.addListener(onMediaQueryChange);
      cleanups.push(function() {
        mobileQuery.removeListener(onMediaQueryChange);
      });
    }

    addListener(window, 'resize', onResize);
    addListener(window, 'pageshow', onPageshow);
    addListener(window, 'focus', onFocus);
    addListener(document, 'visibilitychange', onVisibilityChange);

    initialiseHero();

    return {
      initialise: initialiseHero,
      destroy: function() {
        window.clearTimeout(resizeTimer);
        window.clearTimeout(retryTimer);
        cleanups.forEach(function(cleanup) {
          cleanup();
        });
        cleanups = [];
      }
    };
  }

  function initSection(section) {
    var instance;

    if (!section) {
      return;
    }

    instance = instances.get(section);

    if (instance) {
      instance.initialise();
      return;
    }

    instances.set(section, createHero(section));
  }

  function destroySection(section) {
    var instance = instances.get(section);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(section);
  }

  function initAll(root) {
    var scope = root || document;
    var sections = [];

    if (scope.matches && scope.matches(HERO_SELECTOR)) {
      sections.push(scope);
    }

    scope.querySelectorAll(HERO_SELECTOR).forEach(function(section) {
      sections.push(section);
    });

    sections.forEach(initSection);
  }

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  document.addEventListener('shopify:section:load', function(event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function(event) {
    var section = event.target && event.target.matches && event.target.matches(HERO_SELECTOR)
      ? event.target
      : event.target.querySelector(HERO_SELECTOR);

    destroySection(section);
  });

  window.VletteHomeHero = {
    initAll: initAll,
    initSection: initSection,
    destroySection: destroySection
  };

  ready(function() {
    initAll(document);
  });
})();
